import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
    Col,
    Row,
    ListGroup,
    ListGroupItem,
    Modal,
    Button,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import moment from 'moment';
import { actions } from '../../../context';
import DetailInfoRow from './PluginConfig/DetailInfoRow';
import hasPermission from '../../Shared/hasPermission';
import AddPluginModal from '../../Modals/MissionControl/PluginManager/AddPluginModal';

const formColClass = 'px-2 py-1';

const PluginDetailsModal = (props) => {
    const { isOpen, closeModalBehavior } = props;

    const dispatch = useDispatch();

    const permissions = useSelector(
        (state) => state.auth.permissions,
        shallowEqual
    );
    const pluginId = useSelector(
        (state) => state.missionControl.editCurrentPluginId,
        shallowEqual
    );
    const pluginData = useSelector(
        (state) => state.missionControl.editCurrentPlugin
    );
    const isInactive = useSelector(
        (state) => state.missionControl.isPluginInactive
    );
    const pims = useSelector((state) => state.pims.allPims);

    const [pluginVersions, setPluginVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isEditPluginModalOpen, setIsEditPluginModalOpen] = useState(false);
    const tz = moment().tz(moment.tz.guess()).zoneAbbr();

    const openPluginEditModal = () => {
        closeModalBehavior();
        setIsEditPluginModalOpen(true);
    };

    const canAddEditPlugin = hasPermission(['PluginManagerEdit'], permissions);

    const modalProps = {
        className: 'text-light',
        style: { minWidth: '50vw' },
        backdrop: 'static',
    };

    const dispatchMultiple = async (res) => {
        const actionsToDispatch = [
            dispatch(
                actions.missionControl.setEditModalCurrentPlugin(res.data || {}) //Not sure i need any of these execept getPluginVersions
            ),
            dispatch(
                actions.missionControl.setSelectedPluginDefaultVersion(
                    res.data.defaultVersion?.id
                )
            ),
            dispatch(
                actions.missionControl.setPluginIsInactive(
                    !res.data?.isInactive
                )
            ),
            dispatch(actions.missionControl.getPluginVersions(pluginId)).then(
                (res) => setPluginVersions(res || [])
            ),
        ];

        await Promise.all(actionsToDispatch);
    };

    const refresh = () => {
        if (pluginId) {
            setLoading(true);

            dispatch(actions.missionControl.getPlugin(pluginId))
            .then(async (res) => await dispatchMultiple(res))
            .then(() => setLoading(false));
        } 
    };

    useEffect(() => {
        refresh();

        // eslint-disable-next-line
    }, [dispatch, pluginId, isOpen]);

    const getPimsName = (pimsId) => {
        var matchingPims = pims.find((obj) => obj.id === pimsId);
        return matchingPims !== undefined ? matchingPims.name : 'Undefined';
    };

    const getVersionInfo = (pluginVersion) => {
        if (!pluginVersion) {
            return '';
        }

        return (
            <div>
                {`Version: ${pluginVersion.version} - ${pluginVersion.description}`}
                <br />
                <small>
                    {`Released: ${moment
                        .utc(pluginVersion.releaseDate)
                        .local()
                        .format('M/D/YYYY LT')} ${tz}`}
                </small>
            </div>
        );
    };

    const getPluginGeneralStatus = (discreteStatus) => {
        if (discreteStatus?.isRunning) return 'Running';
        if (discreteStatus?.isInstalled) return 'Installed';
        if (discreteStatus?.isCached) return 'Cached';

        return 'Present in Manifest';
    };

    const getScheduledReleaseTime = (time) => {
        if (time === null)
            return 'None';

        return moment
            .utc(time)
            .local()
            .format('M/D/YYYY LT') + ' ' + tz;
    }

    const pluginDetails = () => {
        return (
            <>
                <DetailInfoRow label="Name:" value={pluginData?.name} />
                <DetailInfoRow label="Uid:" value={pluginData?.id} />
                <DetailInfoRow
                    label="Default Startup Timeout:"
                    value={`${pluginData?.defaultStartupTimeout} seconds`}
                />
                <DetailInfoRow label="Default Plugin Version:">
                    <Col className={formColClass}>
                        {getVersionInfo(pluginData?.defaultVersion)}
                    </Col>
                </DetailInfoRow>
                <DetailInfoRow
                    label="Active:"
                    value={isInactive === true ? 'Yes' : 'No'} //TODO: fis this. This logic is wrong but it's being set wrong (somewhere?) so it's works out
                />
                <DetailInfoRow
                    label="Default Status:"
                    value={getPluginGeneralStatus(pluginData?.defaultStatus)}
                />
                {/* PIMS DEPENDENCIES */}
                <DetailInfoRow label="Pims Dependencies:">
                    <Col className={formColClass}>
                        <ListGroup
                            style={{
                                maxHeight: '80vh',
                                overflowY: 'scroll',
                            }}>
                            {(pluginData?.pimsDependencies || []).map((v) => (
                                <div key={v?.id} className="text-dark">
                                    <ListGroupItem>
                                        <div>{getPimsName(v.pimsId)}</div>
                                    </ListGroupItem>
                                </div>
                            ))}
                        </ListGroup>
                    </Col>
                </DetailInfoRow>
                <DetailInfoRow label="Scheduled Release Time:" value={(getScheduledReleaseTime(pluginData?.soonestScheduledRelease))}></DetailInfoRow>
                <DetailInfoRow label="Active Versions:">
                    <Col className={formColClass}>
                        <ListGroup
                            style={{
                                maxHeight: '40vh',
                                overflowY: 'scroll',
                            }}>
                            {pluginVersions.map((ver) => (
                                <div key={ver?.id} className="text-dark">
                                    <ListGroupItem>
                                        {getVersionInfo(ver)}
                                    </ListGroupItem>
                                </div>
                            ))}
                        </ListGroup>
                    </Col>
                </DetailInfoRow>
                <Row className="mx-auto text-nowrap">
                    <Col xs="12" className="text-center">
                        <div id="clickedText" className="m-2 text-light"></div>
                        <span id="triggerText" className="text-light"></span>
                    </Col>
                </Row>
            </>
        );
    };

    return (
        <>
            <AddPluginModal
                isOpen={isEditPluginModalOpen}
                closeModalBehavior={() => setIsEditPluginModalOpen(false)}
                existingPlugin={pluginData}
            />
            <Modal centered isOpen={isOpen} {...modalProps}>
                <ModalHeader>{'Plugin Details'}</ModalHeader>
                <ModalBody>
                    {loading ? (
                        <div className="loading-spinner spin-animate mx-auto my-4"></div>
                    ) : (
                        pluginDetails()
                    )}
                </ModalBody>
                <ModalFooter>
                    {canAddEditPlugin && (
                        <Button color="primary" onClick={openPluginEditModal}>
                            Edit Plugin
                        </Button>
                    )}
                    <Button color="info" onClick={() => closeModalBehavior()}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default PluginDetailsModal;
